import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLocalStorage, getUser } from '../utils/func';
import config from '../utils/config';
import { subscribeSessionChanges } from '../hooks/useSessionIdentity';
import url from '../utils/url';
import { notificationRequest, notificationTarget } from './api';
import { createNativePush, isNativeIOS } from './nativePush';
import { createAndroidPush, isNativeAndroid, isAndroidApp, subscribeAndroidBridge } from './androidPush';
import { createWebPush, supportsWebPush } from './webPush';
import { isAppActive, subscribeAppActivity } from '../utils/appActivity';
import { useOnboarding } from '../onboarding/OnboardingProvider';

const Context = createContext({ enabled: false, hasUnseen: false, refresh: async () => {}, enablePush: async () => {} });
export const useNotifications = () => useContext(Context);
// Storage can change before React commits the session event (also in another
// TWA/browser tab). Async completions must validate the persisted owner too.
const currentOwner = ({ token, userId }) => token === getLocalStorage(config.token) && userId === getUser()?.id;

export default function NotificationsProvider({ ready, children }) {
  const navigate = useNavigate();
  const { dismiss: dismissOnboarding } = useOnboarding();
  const [, setAuthVersion] = useState(0);
  const [capabilities, setCapabilities] = useState(null);
  const [hasUnseen, setHasUnseen] = useState(false);
  const [action, setAction] = useState(null);
  const [platform, setPlatform] = useState(() => isNativeIOS() ? 'ios' : isAndroidApp() ? 'android' : 'web');
  const [deviceStatus, setDeviceStatus] = useState({ platform: isNativeIOS() ? 'ios' : isAndroidApp() ? 'android' : 'web',
    permission: 'unknown', registration: 'unavailable' });
  const native = useRef(null), refreshRef = useRef(null), epoch = useRef(0), statusRequest = useRef(null);
  const token = getLocalStorage(config.token), userId = getUser()?.id;
  const identity = useRef(null); identity.current = { token, userId };
  const notificationsEnabled = capabilities?.notifications === true;
  const nativeIOSAvailable = capabilities?.native_ios === true, nativeAndroidAvailable = capabilities?.native_android === true;
  const publicKey = capabilities?.vapid_public_key;
  const webAvailable = capabilities?.web_push === true && typeof publicKey === 'string' && publicKey.trim().length > 0 && supportsWebPush();

  useEffect(() => subscribeAndroidBridge(() => {
    if (!isNativeIOS() && isNativeAndroid()) setPlatform('android');
  }), []);

  useEffect(() => {
    const changed = () => {
      if (identity.current.token !== getLocalStorage(config.token) || identity.current.userId !== getUser()?.id) {
        setAuthVersion(value => value + 1);
      }
    };
    return subscribeSessionChanges(changed);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timer, pending = null;
    const load = () => {
      if (controller.signal.aborted || pending) return pending;
      pending = notificationRequest('/capabilities', { signal: controller.signal, token }).then(result => {
        if (!controller.signal.aborted) setCapabilities(previous => previous
          && ['notifications', 'native_ios', 'native_android', 'web_push', 'vapid_public_key'].every(key => previous[key] === result[key]) ? previous : result);
      }).catch(() => {
        if (!controller.signal.aborted) timer = setTimeout(load, 15000);
      }).finally(() => { pending = null; });
      return pending;
    };
    void load();
    const unsubscribe = subscribeAppActivity(({ active }) => { if (active) { clearTimeout(timer); void load(); } });
    return () => { clearTimeout(timer); controller.abort(); unsubscribe(); };
  }, [token, userId]);

  const refresh = useCallback(async (afterChange = false) => {
    if (!ready || !token || !userId || !notificationsEnabled || !isAppActive()
      || identity.current.token !== token || identity.current.userId !== userId || !currentOwner({ token, userId })) return;
    if (statusRequest.current?.token === token && statusRequest.current?.userId === userId) {
      // A receipt/seen write may be newer than the status already in flight.
      statusRequest.current.dirty ||= afterChange === true;
      return statusRequest.current.promise;
    }
    const current = epoch.current;
    const entry = { token, userId, controller: new AbortController(), promise: null };
    statusRequest.current = entry;
    entry.promise = notificationRequest('/status', { token, signal: entry.controller.signal }).then(result => {
      if (!entry.controller.signal.aborted && epoch.current === current && identity.current.token === token && identity.current.userId === userId
        && currentOwner({ token, userId })) {
        setHasUnseen(Boolean(result.has_unseen));
        window.dispatchEvent(new Event('wiw:notifications-refresh'));
      }
    }).catch(() => { /* polling never interrupts the current page/editor */ })
      .finally(() => {
        if (statusRequest.current !== entry) return;
        statusRequest.current = null;
        if (entry.dirty && !entry.controller.signal.aborted) void refreshRef.current?.();
      });
    return entry.promise;
  }, [ready, token, userId, notificationsEnabled]);
  refreshRef.current = refresh;

  useEffect(() => {
    native.current = (platform === 'ios' ? createNativePush : platform === 'android' ? createAndroidPush : createWebPush)(
      { onReceived: () => refreshRef.current?.(true), onAction: value => { dismissOnboarding(); setAction(value); }, onState: next => setDeviceStatus(previous =>
        Object.keys(next).every(key => previous[key] === next[key]) ? previous : next) });
    const instance = native.current;
    return () => { native.current = null; void instance.dispose(); };
  }, [dismissOnboarding, platform]);

  useEffect(() => {
    // Web push clicks carry only identifiers. The existing authenticated API
    // lookup below resolves the destination and verifies the recipient.
    const address = new URL(window.location.href);
    if (!/\/(?:notifications)\/?$/.test(address.pathname)) return;
    const id = Number(address.searchParams.get('notification')), recipient = Number(address.searchParams.get('recipient'));
    if (Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(recipient) && recipient > 0) {
      dismissOnboarding(); setAction({ id, userId: recipient });
      address.searchParams.delete('notification'); address.searchParams.delete('recipient');
      window.history.replaceState(window.history.state, '', address);
    }
  }, [dismissOnboarding]);

  useEffect(() => {
    const currentEpoch = ++epoch.current; setHasUnseen(false);
    const canRegister = platform === 'ios' ? nativeIOSAvailable : platform === 'android' ? nativeAndroidAvailable : webAvailable;
    // UI readiness includes onboarding/language/loading screens. None of those
    // means logout; availability only gates registration, never account identity.
    const session = token && userId ? { token, userId } : null;
    native.current?.setEnabled?.(canRegister);
    native.current?.configure?.(webAvailable ? publicKey : null);
    native.current?.setSession(session).catch(() => {});
    void native.current?.refreshStatus?.();
    void refresh();
    const unsubscribe = subscribeAppActivity(({ active }) => {
      if (active) {
        void refresh(); void native.current?.refreshStatus?.(); native.current?.register(false).catch(() => {});
      } else {
        statusRequest.current?.controller.abort(); statusRequest.current = null;
      }
    });
    const timer = setInterval(refresh, 30000);
    return () => {
      epoch.current = currentEpoch + 1; clearInterval(timer); unsubscribe();
      statusRequest.current?.controller.abort(); statusRequest.current = null;
    };
  }, [ready, token, userId, nativeIOSAvailable, nativeAndroidAvailable, webAvailable, publicKey, refresh, platform]);

  useEffect(() => {
    if (!action || !ready) return;
    if (!token || !userId) { navigate(`${url('/login')}?from=${encodeURIComponent(url('/notifications'))}`); return; }
    if (action.userId !== userId) { setAction(null); return; }
    let active = true;
    notificationRequest(`/${action.id}`, { token }).then(result => {
      if (!active || !currentOwner({ token, userId })) return;
      const target = notificationTarget(result.notification?.target);
      setAction(null); navigate(url(target || '/notifications'));
    }).catch(() => { if (active && currentOwner({ token, userId })) { setAction(null); navigate(url('/notifications')); } });
    return () => { active = false; };
  }, [action, ready, token, userId, navigate]);

  const enablePush = useCallback(async () => {
    if (isAndroidApp() && !isNativeAndroid()) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
    if (!(platform === 'ios' ? nativeIOSAvailable : platform === 'android' ? nativeAndroidAvailable : webAvailable)) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
    const instance = native.current, owner = identity.current;
    if (!instance) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
    try {
      await instance.register(true);
      if (native.current !== instance || !currentOwner(owner)) throw new Error('PUSH_SESSION_CHANGED');
    }
    catch (error) {
      if (error.message !== 'PUSH_SETTINGS_REQUIRED') throw error;
      if (native.current !== instance || !currentOwner(owner)) throw new Error('PUSH_SESSION_CHANGED');
      await instance.openSettings();
      return { settingsOpened: true };
    }
  }, [nativeIOSAvailable, nativeAndroidAvailable, webAvailable, platform]);
  const refreshDeviceStatus = useCallback(() => native.current?.refreshStatus?.(), []);
  const openPushSettings = useCallback(() => native.current?.openSettings?.(), []);
  const available = capabilities === null ? null : deviceStatus.platform === 'ios' ? nativeIOSAvailable
    : deviceStatus.platform === 'android' ? isNativeAndroid() && nativeAndroidAvailable : webAvailable;
  const value = useMemo(() => ({ enabled: notificationsEnabled, hasUnseen, refresh, enablePush,
    deviceStatus: { ...deviceStatus, available }, refreshDeviceStatus, openPushSettings }),
  [notificationsEnabled, hasUnseen, refresh, enablePush, deviceStatus, available, refreshDeviceStatus, openPushSettings]);
  return <Context.Provider value={value}>
    {children}
  </Context.Provider>;
}
